import BRANDS, { type BrandName } from "@elit-hair/brands";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Button,
    Icon,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from "@elit-hair/elit-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertCircle, TbChevronDown } from "react-icons/tb";

import { cn } from "@/lib/utils";

type BrandSelectorProps = {
    /** Currently selected brand key */
    selectedBrand: BrandName | null;
    /** Callback when brand selection changes */
    onBrandChange: (brand: BrandName) => void;
    /** Whether the sidebar is collapsed */
    isCollapsed?: boolean;
};

export const BrandSelector = ({ selectedBrand, onBrandChange, isCollapsed = false }: BrandSelectorProps) => {
    const [isBrandPopoverOpen, setIsBrandPopoverOpen] = useState(false);
    const [pendingBrand, setPendingBrand] = useState<BrandName | null>(null);
    const { t } = useTranslation();

    // Get all available brands
    const availableBrands = (Object.keys(BRANDS) as Array<BrandName>).filter((brand) => {
        const brandData = BRANDS[brand];
        return brandData && (brandData.displayName || brandData.name);
    });

    // Get selected brand data
    const selectedBrandData = selectedBrand && selectedBrand in BRANDS ? BRANDS[selectedBrand] : null;
    const selectedDisplayName = selectedBrandData?.displayName ?? selectedBrandData?.name ?? "";

    const pendingBrandData = pendingBrand && pendingBrand in BRANDS ? BRANDS[pendingBrand] : null;
    const pendingDisplayName = pendingBrandData?.displayName ?? pendingBrandData?.name ?? "";

    const handleBrandSelect = (brand: BrandName) => {
        setIsBrandPopoverOpen(false);

        if (brand === selectedBrand) {
            return;
        }

        // First selection does not need confirmation
        if (!selectedBrand) {
            onBrandChange(brand);
            return;
        }

        setPendingBrand(brand);
    };

    const handleConfirmBrandChange = () => {
        if (!pendingBrand) {
            return;
        }

        onBrandChange(pendingBrand);
        setPendingBrand(null);
    };

    const handleCancelBrandChange = () => {
        setPendingBrand(null);
    };

    return (
        <>
            <SidebarMenu>
                <SidebarMenuItem>
                    <Popover open={isBrandPopoverOpen} onOpenChange={setIsBrandPopoverOpen}>
                        <PopoverTrigger asChild>
                            <SidebarMenuButton
                                className="text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                                tooltip={isCollapsed ? t("select_brand") : undefined}
                            >
                                {selectedBrand && selectedBrandData ? (
                                    <div className="flex items-center justify-between w-full">
                                        <div className="flex items-center gap-2">
                                            {selectedBrandData.logoSquare ? (
                                                <img
                                                    src={selectedBrandData.logoSquare}
                                                    alt={selectedDisplayName}
                                                    className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] bg-gray-200"
                                                />
                                            ) : (
                                                <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                                                    {selectedDisplayName?.charAt(0) ?? "?"}
                                                </div>
                                            )}
                                            <span>{selectedDisplayName}</span>
                                        </div>
                                        <Icon icon={TbChevronDown} size={20} />
                                    </div>
                                ) : (
                                    <>
                                        <Icon icon={TbAlertCircle} size={20} />
                                        <span>{t("no_brand_selected")}</span>
                                    </>
                                )}
                            </SidebarMenuButton>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2 overflow-hidden" align="start" sideOffset={5}>
                            <div className="flex flex-col">
                                <p className="text-sm text-muted-foreground opacity-50 p-1">{t("select_brand")}</p>
                                <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
                                    {availableBrands.map((brand) => {
                                        const brandData = BRANDS[brand];
                                        if (!brandData) return null;

                                        const displayName = brandData.displayName ?? brandData.name;
                                        if (!displayName) return null;

                                        return (
                                            <Button
                                                key={brand}
                                                variant="ghost"
                                                className={cn(
                                                    "w-full justify-start gap-2 px-4 py-2.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer",
                                                    selectedBrand === brand && "bg-muted text-foreground"
                                                )}
                                                onClick={() => handleBrandSelect(brand)}
                                            >
                                                {brandData.logoSquare ? (
                                                    <img
                                                        src={brandData.logoSquare}
                                                        alt={displayName}
                                                        className="h-6 w-6 bg-gray-200 rounded-full"
                                                    />
                                                ) : (
                                                    <div className="h-6 w-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                                        {displayName.charAt(0)}
                                                    </div>
                                                )}
                                                <span className="font-medium">{displayName}</span>
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                </SidebarMenuItem>
            </SidebarMenu>

            <AlertDialog
                open={pendingBrand !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        handleCancelBrandChange();
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("confirm_brand_change_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("confirm_brand_change_description", { brand: pendingDisplayName })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmBrandChange}>
                            {t("confirm_brand_change")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
